import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";

interface UserData {
  name: string;
  email: string;
  userId: string;
  role: string;
}

interface Candidate {
  id: string;
  name: string;
  phoneNo: string;
  education: string;
  image: string;
  createDateTime: string;
  lastUpdatedDateTime: string;
  totalMarks?: number;
  status?: "Pass" | "Fail" | null;
  warningCount?: number;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    username: string;
    email: string;
    userId: string;
    role: string;
  };
}


const PlacementDashboard = () => {
  const navigate = useNavigate();
  const [userId] = useState<string>("123");
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState<boolean>(true);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);

  // Filter states
  const [searchInput, setSearchInput] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [educationFilter, setEducationFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [uniqueEducations, setUniqueEducations] = useState<string[]>([]);

  // Pagination states
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [totalCandidates, setTotalCandidates] = useState<number>(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchInput);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Role-based redirect function
  const redirectToLogin = () => {
    window.location.href = "/placement/login";
  };

  const performLogout = useCallback(() => {
    // Clear user data from localStorage
    localStorage.removeItem("userId");
    localStorage.removeItem("userToken"); // if you store tokens

    // Redirect to login
    redirectToLogin();
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userId = localStorage.getItem("userId");

        if (!userId) {
          // No userId in localStorage, redirect to login
          redirectToLogin();
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/login/user/${userId}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const result: LoginResponse = await response.json();
        if (result.success && result.data) {
          const user = {
            name: result.data.username,
            email: result.data.email,
            userId: result.data.userId,
            role: result.data.role,
          };

          // Check if user role is placement (case-insensitive)
          if (user.role.toLowerCase() !== "placement") {
            // User is not a placement, perform logout and redirect
            alert("Access denied. Only placement can access this dashboard.");
            performLogout();
            return;
          }

          setUserData(user);
        } else {
          throw new Error(result.message || "Failed to fetch user data");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        console.error("Error fetching user data:", errorMessage);

        // On error, also redirect to login
        alert("Authentication failed. Please login again.");
        performLogout();
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, performLogout]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, educationFilter, statusFilter]);

  // Fetch candidates data
  useEffect(() => {
    // Only fetch candidates if user is authenticated and is a placement
    if (!userData || userData.role.toLowerCase() !== "placement") {
      return;
    }

    const fetchCandidates = async () => {
      try {
        setCandidatesLoading(true);
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);
        if (educationFilter) params.append("education", educationFilter);
        if (statusFilter) params.append("status", statusFilter);

        const response = await fetch(
          `${API_BASE_URL}/registration?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch candidates data");
        }

        const result = await response.json();

        // Backend returns { data, total, uniqueEducations } when paginated
        setCandidates(result.data || []);
        setFilteredCandidates(result.data || []);
        setTotalCandidates(result.total || 0);

        if (result.uniqueEducations) {
          setUniqueEducations(result.uniqueEducations);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        setCandidatesError(errorMessage);
        console.error("Error fetching candidates:", errorMessage);
      } finally {
        setCandidatesLoading(false);
      }
    };

    fetchCandidates();
  }, [userData, page, limit, debouncedSearchTerm, educationFilter, statusFilter]);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    performLogout();
  };

  const handleDeleteCandidate = async (
    candidateId: string,
    candidateName: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // Prevent row click navigation
    if (
      window.confirm(
        `Are you sure you want to delete candidate "${candidateName}" and all their test data? This action cannot be undone.`
      )
    ) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/screening-questions/${candidateId}`,
          {
            method: "DELETE",
          }
        );
        if (!response.ok) {
          throw new Error("Failed to delete candidate");
        }
        const data = await response.json();
        if (data.success) {
          alert("Candidate deleted successfully");
          setCandidates((prev) => prev.filter((c) => c.id !== candidateId));
          setFilteredCandidates((prev) =>
            prev.filter((c) => c.id !== candidateId)
          );
          setTotalCandidates((prev) => prev - 1);
        } else {
          alert(data.message || "Failed to delete candidate");
        }
      } catch (err) {
        alert(
          err instanceof Error ? err.message : "Error deleting candidate"
        );
      }
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getStatusBadge = (status: "Pass" | "Fail" | null) => {
    if (!status) return null;

    switch (status) {
      case "Pass":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <svg
              className="h-3 w-3 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Pass
          </span>
        );
      case "Fail":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg
              className="h-3 w-3 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            Fail
          </span>
        );
      default:
        return null;
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setDebouncedSearchTerm("");
    setEducationFilter("");
    setStatusFilter("");
  };

  if (loading || (candidatesLoading && candidates.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // If userData is null at this point, it means we're in the process of redirecting
  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <img
                  src="https://res.cloudinary.com/dureqzdco/image/upload/v1735905040/Image/r1u63eh2cz9nt9kcocr2.png"
                  alt=""
                  className="h-10 w-auto"
                />
              </div>
            </div>

            {/* User Info & Actions */}
            <div className="flex items-center space-x-4">
              {userData && (
                <div className="relative">
                  {/* User Avatar/Button */}
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {userData.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium">{userData.name}</span>
                    <svg
                      className={`h-4 w-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="py-1">
                        {/* User Info Section */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="text-sm font-semibold text-gray-900">
                            {userData.name}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {userData.email}
                          </div>
                          <div className="flex items-center mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {userData.role}
                            </span>
                            <span className="ml-2 text-xs text-gray-400">
                              ({userData.userId})
                            </span>
                          </div>
                        </div>

                        {/* Logout Button */}
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-800 transition-colors flex items-center space-x-2"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          <span className="font-medium">Logout</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Backdrop to close dropdown */}
                  {isDropdownOpen && (
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsDropdownOpen(false)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Section */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search by name, phone, or ID..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Education Filter */}
              <select
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={educationFilter}
                onChange={(e) => setEducationFilter(e.target.value)}
              >
                <option value="">All Education</option>
                {uniqueEducations.map((education) => (
                  <option key={education} value={education}>
                    {education}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
              </select>

              {/* Clear Filters */}
              {(searchInput || educationFilter || statusFilter) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Candidates List
            </h2>
            <div className="flex items-center justify-between mt-1">
              <p className="text-sm text-gray-500">
                Showing {filteredCandidates.length} of {totalCandidates} candidates
              </p>
              {candidatesError && (
                <p className="text-sm text-red-600">
                  Error loading candidates: {candidatesError}
                </p>
              )}
            </div>
          </div>

          <div className={`overflow-x-auto transition-opacity duration-200 ${candidatesLoading ? "opacity-50 pointer-events-none" : ""}`}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Education
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Marks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Warnings
                  </th>
                  {userData?.email === "admin@navishr.com" && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCandidates.map((candidate) => (
                  <tr
                    key={candidate.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/placement/result?candidateId=${encodeURIComponent(
                          candidate.id
                        )}`
                      )
                    }
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-12 w-12 flex-shrink-0">
                          <img
                            className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                            src={candidate.image}
                            alt={candidate.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                candidate.name
                              )}&background=3b82f6&color=ffffff`;
                            }}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {candidate.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {candidate.id.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-sm text-gray-900">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span>{candidate.phoneNo}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-sm text-gray-900">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 14l9-5-9-5-9 5 9 5z"></path>
                          <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                          ></path>
                        </svg>
                        <span>{candidate.education}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {candidate.totalMarks !== undefined
                          ? `${Math.ceil(candidate.totalMarks)}/90`
                          : "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {candidate.status && getStatusBadge(candidate.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(candidate.warningCount || 0) > 0
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                          }`}
                      >
                        <svg
                          className="h-3 w-3 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {candidate.warningCount || 0}
                      </span>
                    </td>
                    {userData?.email === "admin@navishr.com" && (
                      <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                        <button
                          onClick={(e) =>
                            handleDeleteCandidate(candidate.id, candidate.name, e)
                          }
                          className="text-red-600 hover:text-red-950 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors font-semibold"
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          {totalCandidates > limit && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((prev) => Math.min(prev + 1, Math.ceil(totalCandidates / limit)))}
                  disabled={page >= Math.ceil(totalCandidates / limit)}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(page - 1) * limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(page * limit, totalCandidates)}
                    </span>{" "}
                    of <span className="font-medium">{totalCandidates}</span> results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {Array.from({ length: Math.ceil(totalCandidates / limit) }).map((_, idx) => {
                      const pNum = idx + 1;
                      if (Math.abs(pNum - page) <= 2 || pNum === 1 || pNum === Math.ceil(totalCandidates / limit)) {
                        return (
                          <button
                            key={pNum}
                            onClick={() => setPage(pNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === pNum
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                          >
                            {pNum}
                          </button>
                        );
                      }
                      if (pNum === 2 || pNum === Math.ceil(totalCandidates / limit) - 1) {
                        return (
                          <span key={pNum} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    <button
                      onClick={() => setPage((prev) => Math.min(prev + 1, Math.ceil(totalCandidates / limit)))}
                      disabled={page >= Math.ceil(totalCandidates / limit)}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredCandidates.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">
              {candidates.length === 0
                ? "No candidates found"
                : "No candidates match your filters"}
            </div>
            <p className="text-gray-500 mt-2">
              {candidates.length === 0
                ? "Candidates will appear here once they are added to the system."
                : "Try adjusting your search terms or filters to see more results."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacementDashboard;
